import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { fetcher } from '@weezevent/weezjs-core';

import { Role, UserV1 } from 'models';
import { usePermissions } from 'models/user/permissions';

import { useFilters, usePageSize, useCurrentPage, useSearch, handleSearchQuery } from 'hooks/useFilters';

import { WeezHeader, Icon, Card, Button, ContentCard, Filter, Table, TableCellDropdown, Dropdown, ToastContext, FilterSetController } from '@weezevent/nacre';

import { LoadingTable, Paginator, SauronEmptyCard, PaginationDropDown, QuickFilters } from 'components';
import NewUser from 'pages/usersV1/user/NewUser';
import RoleCreationModal from 'pages/usersV1/user/roles/RoleCreationModal';

import { listSelection, requestCount } from 'utils/utils';

import css from 'pages/usersV1/list.module.css';

const POSSIBLE_PER_PAGE_COUNT = [10, 50, 100];

const spec = {
    multiple: ['roles__slug__in'],
    fields: ['roles__slug__in', 'same_level_users', 'search'],
    date: [],
    form: [],
    aliases: {}
};

export const UsersList = ({ organizationId = null, useCase }) => {
    const [resourceKey, resourceKey_singular] = React.useMemo(() => [useCase, useCase === 'applications' ? 'application' : 'user'], [useCase]);
    const history = useHistory();
    const { t } = useTranslation();
    const user = usePermissions();
    const toast = React.useContext(ToastContext);
    const [userRoleModal, setUserRoleModal] = React.useState({ user: null, open: false });
    const [usersIds, setUsersIds] = React.useState([]);
    const [selectedUsers, setSelectedUsers] = React.useState([]);
    const [newUserModal, setNewUserModal] = React.useState(false);

    const [filters, setFilters] = useFilters(spec, {});
    const [perPageCount, setPerPageCount] = usePageSize(10);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [search, setSearch] = useSearch();

    const [users, loadingUsers, syncUsers] = UserV1.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId,
            useCase: useCase,
            query: {
                limit: perPageCount,
                offset: currentPage * perPageCount,
                search,
                ...handleSearchQuery(spec, filters, true)
            },
            onSuccess: response => {
                setUsersIds(response.map(user => user.username));
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [filters, perPageCount, currentPage, search, organizationId, useCase]
    );

    const usersCount = React.useMemo(() => {
        return requestCount(loadingUsers, users);
    }, [users, loadingUsers]);

    const handleQuickFilter = React.useCallback(
        checked => {
            if (checked) {
                setFilters({ ...filters, same_level_users: checked });
            } else {
                const { same_level_users, ...rest } = filters;
                setFilters(rest);
            }
        },
        [filters, setFilters]
    );

    const handleSelect = React.useCallback(
        user => {
            setSelectedUsers(listSelection(selectedUsers, usersIds, user));
        },
        [selectedUsers, usersIds, setSelectedUsers]
    );

    const [roles] = Role.useApiModel({ cache: false, allow_cache: false, organizationId }, [organizationId]);
    const roleOptions = React.useMemo(() => roles?.map(r => r.toOption()), [roles]);

    return (
        <>
            <WeezHeader
                rightComponent={[<Button key={0} primary icon={<Icon name={'plus'} />} label={t(`sauron.${resourceKey}.add`)} onClick={() => setNewUserModal(true)} />]}
                title={t(`common.${resourceKey}`)}
                backLink={
                    organizationId && {
                        onClick: () => {
                            history.push(`/organizations`);
                        }
                    }
                }
            >
                {t(`sauron.${resourceKey}.list`)}
            </WeezHeader>

            <Card>
                <ContentCard>
                    <FilterSetController
                        total={usersCount}
                        labelFilters={t('sauron.users.filters.label-filter')}
                        labelTotal={t(`sauron.${resourceKey}.${resourceKey_singular}`, { count: usersCount })}
                        initialValues={filters}
                        onChange={setFilters}
                        onReset={() => {
                            setFilters({});
                            setCurrentPage(0);
                            setPerPageCount(10);
                        }}
                        search={{
                            onChange: (_, value) => setSearch(value),
                            handleReset: () => setSearch(''),
                            placeholder: t('common.search'),
                            value: search
                        }}
                    >
                        <Filter
                            index={0}
                            name={'roles__slug__in'}
                            isSearchable
                            multiple
                            labelName={t('sauron.users.filters.roles.label')}
                            labelCTA={t('sauron.users.filters.label-cta')}
                            options={roleOptions || []}
                        />
                    </FilterSetController>
                    <PaginationDropDown
                        title={t(`sauron.${resourceKey}.${resourceKey_singular}_action`, { count: selectedUsers.length })}
                        tooltip={t(`sauron.${resourceKey}.no_action_tooltip`)}
                        label={`sauron.${resourceKey}.page_size`}
                        selected={selectedUsers}
                        count={usersCount}
                        disabled
                        limit={perPageCount}
                        pageSizes={POSSIBLE_PER_PAGE_COUNT}
                        onChange={setPerPageCount}
                    />
                    {!organizationId && user.isAdmin() && useCase === 'users' && (
                        <QuickFilters>
                            <QuickFilters.Item label={t(`sauron.${resourceKey}.quick-filter`)} active={!!filters.same_level_users} onToggle={handleQuickFilter} />
                        </QuickFilters>
                    )}
                </ContentCard>
                {newUserModal && (
                    <NewUser organizationId={organizationId} useCaseItems={[resourceKey, resourceKey_singular]} open={newUserModal} onClose={() => setNewUserModal(false)} />
                )}
                <Table useNew selectable onSelectAll={() => handleSelect()} className={css['users-table']}>
                    <Table.THead>
                        <Table.Tr isHeader active={usersIds.length > 0 && usersIds.every(id => selectedUsers.includes(id))}>
                            <th className={css['users-table-medium']}>{t(`common.${resourceKey_singular}`)}</th>
                            <th className={css['users-table-medium']}>{t(`common.email`)}</th>
                            <th className={css['users-table-medium']}>{t(`common.roles`)}</th>
                            <th className={css['users-table-small']}>{t(`common.actions`)}</th>
                        </Table.Tr>
                    </Table.THead>
                    <Table.TBody>
                        {!loadingUsers &&
                            users?.length > 0 &&
                            users?.map(_user => (
                                <UserItem
                                    key={_user.username}
                                    authenticatedUser={user}
                                    user={_user}
                                    sync={syncUsers}
                                    selected={selectedUsers}
                                    onSelect={handleSelect}
                                    onOpen={setUserRoleModal}
                                    segment={[resourceKey, resourceKey_singular]}
                                />
                            ))}
                    </Table.TBody>
                </Table>
                <RoleCreationModal open={userRoleModal.open} user={userRoleModal.user} onClose={() => setUserRoleModal({ user: null, open: false })} sync={syncUsers} />
                <LoadingTable loading={loadingUsers} height="30vh" />
                {!loadingUsers &&
                    (users?.length > 0 ? (
                        <Paginator currentPage={currentPage} onPageChange={setCurrentPage} pageSize={perPageCount} totalCount={usersCount} />
                    ) : (
                        <SauronEmptyCard title={t(`sauron.${resourceKey}.empty-card-title`)} subTitle={t(`sauron.${resourceKey}.empty-card-subtitle`)} />
                    ))}
            </Card>
        </>
    );
};

const UserItem = ({ user, sync, authenticatedUser, selected, onSelect, onOpen, segment }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const [resourceKey, resourceKey_singular] = segment;
    const resolvedUser = React.useMemo(() => {
        return {
            name: user.resolvedName,
            email: user.email || '-',
            roles: user.user_roles?.filter(role => role.context.level <= 3 && !role.context.parents?.some(p => p.value !== '*')).length
        };
    }, [user]);

    const handleRedirect = React.useCallback(() => {
        history.push(`${resourceKey}/${user.id || user.username}`);
    }, [user]);

    const handleDeleteUser = React.useCallback(() => {
        const url = resourceKey === 'applications' ? `/ticket/v1/applications/${user.id}` : `/ticket/users/${user.username}`;

        fetcher
            .delete(url)
            .then(response => {
                if (response.status === 204) {
                    toast.success(t(`sauron.toasts.${resourceKey}.delete-${resourceKey_singular}-success`));
                }
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => {
                sync();
            });
    }, [user.id, user.username, resourceKey, resourceKey_singular]);

    return (
        <Table.Tr onSelected={() => onSelect(user.username)} active={selected.includes(user.username)} onClick={handleRedirect}>
            <Table.Td>{resolvedUser.name}</Table.Td>
            <Table.Td>{resolvedUser.email}</Table.Td>
            <Table.Td>{`${resolvedUser.roles} ${t(`sauron.${resourceKey}.role`, { count: resolvedUser.roles })}`}</Table.Td>

            <TableCellDropdown>
                <Dropdown.Item item={t(`sauron.${resourceKey_singular}.see-${resourceKey_singular}`)} onClick={handleRedirect} />
                <Dropdown.Item item={t(`sauron.${resourceKey_singular}.add-role`)} onClick={() => onOpen({ user, open: true })} disabled={!authenticatedUser.canManageRoles()} />
                {authenticatedUser.isAdmin() && resourceKey_singular === 'application' && (
                    <Dropdown.Item deleteItem={true} item={t(`sauron.${resourceKey_singular}.delete`)} onClick={() => handleDeleteUser()} />
                )}
            </TableCellDropdown>
        </Table.Tr>
    );
};
